/**
 * The learned forecast model: a versioned set of weights that forecastVote() applies on top of the hand-tuned
 * factor math (forecast.ts). PRIOR_MODEL reproduces today's forecast exactly (weight 1 on each of the four
 * original factors, 0 on every new one); the nightly trainer (train.ts, worker/src/producers/forecast-train.js)
 * fits a new set toward this prior and promotes it only when it beats the active one out of sample.
 *
 * Everything learned lives in the YES frame (positive = toward a yes vote); forecastVote() flips it for an
 * "oppose" position, so the position-flip symmetry of the hand model survives.
 */

export type BaseFactorId = "similar" | "moneyLinked" | "leaning" | "tension";                                       // the four original factors
export type NewFactorId = "sponsor" | "committee" | "said" | "mayor" | "allies" | "wardVoice" | "moneyAmount" | "graph"      // learned from evidence
  | "homeWard" | "procedure" | "wardElectorate" | "wardDemographics"                                                     // public data (scripts/fetch_public.py)
  | "mayorStatement"                                                                                                     // the Mayor's YouTube channel (scripts/fetch_youtube.py)
  | "leaning2";                                                                                                          // the council's second vote axis
export type FactorId = BaseFactorId | NewFactorId;

/** The four original factors, in the order the Why panel shows them. */
export const BASE_FACTORS: BaseFactorId[] = ["moneyLinked", "leaning", "similar", "tension"];
/** The new factors (prior weight 0): shown only when their evidence is non-zero. */
export const NEW_FACTORS: NewFactorId[] = ["sponsor", "committee", "said", "mayor", "allies", "wardVoice", "moneyAmount", "graph", "homeWard", "procedure", "wardElectorate", "wardDemographics", "mayorStatement", "leaning2"];
export const FACTOR_IDS: FactorId[] = [...BASE_FACTORS, ...NEW_FACTORS];
/** Factors whose weight may also change with council tension (a slope on T). */
export const INTERACTIONS: FactorId[] = ["leaning", "similar", "sponsor", "allies"];
/** Factors a retrospective backtest cannot see without leaking the outcome (graph embeddings encode past votes). */
export const LIVE_ONLY: FactorId[] = ["graph"];

/** Topics an item can belong to (worker/src/tension.js topicOf), for topic-specific weights. */
export const TOPICS = ["housing", "budget", "licenses", "other"] as const;
export type Topic = (typeof TOPICS)[number];
/** Factors that may weigh differently by topic (money and leaning matter more on some topics than others). */
export const TOPIC_FACTORS: FactorId[] = ["similar", "moneyLinked", "leaning"];
export const topicOfInput = (t: string | null | undefined): Topic => (TOPICS as readonly string[]).includes(String(t)) ? (t as Topic) : "other";

export interface ForecastModel {
  version: string;                       // "prior" | "v2026-10-10.1"
  trainedAt: string | null;
  weights: Record<FactorId, number>;     // β_j: prior 1 for the four original factors, 0 for the new ones
  tensionSlopes: Record<FactorId, number>; // γ_j on T, prior 0 (only INTERACTIONS are ever non-zero)
  intercept: number;                     // yes frame, prior 0
  members: Record<number, number>;       // ward → u_w (yes frame), prior 0; {} = no member effects
  rangeScale: number;                    // κ on the hand range formula, prior 1
  /** Topic-specific additions to the weights of TOPIC_FACTORS, prior 0; {} = none. */
  topicWeights: Partial<Record<Topic, Partial<Record<FactorId, number>>>>;
  /** Extra range half-width by yes-probability decile (0–10%, 10–20%, …), learned from walk-forward residuals; null = none. */
  rangeBins: number[] | null;
  /** The recipe's time decay (half-life in days) the weights were fit with; informational. */
  decayDays: number | null;
}

const zeros = () => Object.fromEntries(FACTOR_IDS.map((id) => [id, 0])) as Record<FactorId, number>;

export const PRIOR_MODEL: ForecastModel = Object.freeze({
  version: "prior",
  trainedAt: null,
  weights: Object.freeze({ ...zeros(), similar: 1, moneyLinked: 1, leaning: 1, tension: 1 }) as Record<FactorId, number>,
  tensionSlopes: Object.freeze(zeros()) as Record<FactorId, number>,
  intercept: 0,
  members: Object.freeze({}) as Record<number, number>,
  rangeScale: 1,
  topicWeights: Object.freeze({}) as ForecastModel["topicWeights"],
  rangeBins: null,
  decayDays: null,
});

const num = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);

/** True when a model is the prior in effect (every parameter at its prior value): forecastVote() then takes the hand path. */
export function isPrior(m: ForecastModel): boolean {
  if (m === PRIOR_MODEL) return true;
  if (m.intercept !== 0 || m.rangeScale !== 1 || Object.keys(m.members).length || m.rangeBins?.some((x) => x)) return false;
  if (Object.values(m.topicWeights || {}).some((t) => Object.values(t || {}).some((x) => x))) return false;
  return FACTOR_IDS.every((id) => m.weights[id] === PRIOR_MODEL.weights[id] && m.tensionSlopes[id] === 0);
}

/** Parse a KV/D1 JSON value into a model; anything malformed falls back to the prior (never throws). */
export function hydrateModel(json: unknown): ForecastModel {
  if (!json || typeof json !== "object") return PRIOR_MODEL;
  const j = json as Record<string, unknown>;
  const w = (j.weights || {}) as Record<string, unknown>, s = (j.tensionSlopes || {}) as Record<string, unknown>;
  const weights = zeros(), tensionSlopes = zeros();
  for (const id of FACTOR_IDS) { weights[id] = num(w[id], PRIOR_MODEL.weights[id]); tensionSlopes[id] = num(s[id], 0); }
  const members: Record<number, number> = {};
  for (const [k, v] of Object.entries((j.members || {}) as Record<string, unknown>)) {
    const ward = Number(k), u = num(v, 0);
    if (ward >= 1 && ward <= 50 && u !== 0) members[ward] = u;
  }
  const topicWeights: ForecastModel["topicWeights"] = {};
  for (const t of TOPICS) {
    const src = ((j.topicWeights || {}) as Record<string, unknown>)[t];
    if (!src || typeof src !== "object") continue;
    const out: Partial<Record<FactorId, number>> = {};
    for (const id of TOPIC_FACTORS) { const x = num((src as Record<string, unknown>)[id], 0); if (x) out[id] = x; }
    if (Object.keys(out).length) topicWeights[t] = out;
  }
  const rb = Array.isArray(j.rangeBins) && j.rangeBins.length === 10 ? j.rangeBins.map((x) => Math.max(0, Math.min(0.3, num(x, 0)))) : null;
  return {
    version: typeof j.version === "string" && j.version ? j.version : "prior",
    trainedAt: typeof j.trainedAt === "string" ? j.trainedAt : null,
    weights, tensionSlopes, intercept: num(j.intercept, 0), members, rangeScale: Math.max(0.3, Math.min(3, num(j.rangeScale, 1))),
    topicWeights, rangeBins: rb && rb.some((x) => x) ? rb : null, decayDays: typeof j.decayDays === "number" && j.decayDays > 0 ? j.decayDays : null,
  };
}

/** Prior precision (1/σ²) per parameter kind, before priorScale. */
export const PRIOR_PRECISION = { base: 40, newFactor: 10, slope: 10, intercept: 40, member: 25, topic: 40 };

export interface Layout { priorScale: number; memberEffects: boolean; topicWeights: boolean }

/** Dense parameters: [β_j for FACTOR_IDS] ++ [γ_j for INTERACTIONS] ++ [intercept]; with topic weights, ++ [TOPICS × TOPIC_FACTORS]. */
export const DENSE_BASE = FACTOR_IDS.length + INTERACTIONS.length + 1;
export const TOPIC_PARAMS = TOPICS.length * TOPIC_FACTORS.length;
export const denseParams = (l: Pick<Layout, "topicWeights">) => DENSE_BASE + (l.topicWeights ? TOPIC_PARAMS : 0);
/** Kept for callers that predate topic weights: the dense size without them. */
export const DENSE_PARAMS = DENSE_BASE;

/** Prior means and precisions in parameter order, scaled by `priorScale`. */
export function priorSpec(priorScale = 1, memberEffects = false, topicWeights = false): { names: string[]; mean: number[]; precision: number[] } {
  const names: string[] = [], mean: number[] = [], precision: number[] = [];
  for (const id of FACTOR_IDS) {
    names.push(`w:${id}`); mean.push(PRIOR_MODEL.weights[id]);
    precision.push(((BASE_FACTORS as FactorId[]).includes(id) ? PRIOR_PRECISION.base : PRIOR_PRECISION.newFactor) * priorScale);
  }
  for (const id of INTERACTIONS) { names.push(`t:${id}`); mean.push(0); precision.push(PRIOR_PRECISION.slope * priorScale); }
  names.push("intercept"); mean.push(0); precision.push(PRIOR_PRECISION.intercept * priorScale);
  if (topicWeights) for (const t of TOPICS) for (const id of TOPIC_FACTORS) { names.push(`${t}:${id}`); mean.push(0); precision.push(PRIOR_PRECISION.topic * priorScale); }
  if (memberEffects) for (let w = 1; w <= 50; w++) { names.push(`u:${w}`); mean.push(0); precision.push(PRIOR_PRECISION.member * priorScale); }
  return { names, mean, precision };
}

/** A model from a parameter vector in priorSpec() order. */
export function modelFromTheta(theta: number[], layout: Pick<Layout, "memberEffects" | "topicWeights">, version: string, trainedAt: string | null, rangeScale = 1): ForecastModel {
  const weights = zeros(), tensionSlopes = zeros();
  let i = 0;
  for (const id of FACTOR_IDS) weights[id] = theta[i++];
  for (const id of INTERACTIONS) tensionSlopes[id] = theta[i++];
  const intercept = theta[i++];
  const topicWeights: ForecastModel["topicWeights"] = {};
  if (layout.topicWeights) for (const t of TOPICS) {
    const out: Partial<Record<FactorId, number>> = {};
    for (const id of TOPIC_FACTORS) { const x = theta[i++]; if (x) out[id] = x; }
    if (Object.keys(out).length) topicWeights[t] = out;
  }
  const members: Record<number, number> = {};
  if (layout.memberEffects) for (let w = 1; w <= 50; w++) { const u = theta[i++]; if (u) members[w] = u; }
  return { version, trainedAt, weights, tensionSlopes, intercept, members, rangeScale, topicWeights, rangeBins: null, decayDays: null };
}

/** The parameter vector of a model in priorSpec() order. */
export function thetaFromModel(m: ForecastModel, layout: Pick<Layout, "memberEffects" | "topicWeights">): number[] {
  const out: number[] = [];
  for (const id of FACTOR_IDS) out.push(m.weights[id]);
  for (const id of INTERACTIONS) out.push(m.tensionSlopes[id]);
  out.push(m.intercept);
  if (layout.topicWeights) for (const t of TOPICS) for (const id of TOPIC_FACTORS) out.push(m.topicWeights?.[t]?.[id] ?? 0);
  if (layout.memberEffects) for (let w = 1; w <= 50; w++) out.push(m.members[w] ?? 0);
  return out;
}
